import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  IconButton,
} from '@mui/material'
import React from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from '../../../../../common/hooks/helper/useTranslation'
import { TextfieldController } from '../../../../../common/components'
import { ContactType } from './patientContacts.types'
import { CloseIcon } from '../../../../../common/icons/CloseIcon'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { useMutation } from '@apollo/client'
import {
  contactChore,
  updatePatientProfile,
  updatePatientProfileVariables,
} from '../../../../../models/graphqlTypes'
import { UPDATE_PATIENT_PROFILE } from '../../../../../operations'
import { useNotifications } from '../../../../../common/providers'
import { ValidPhonePattern } from '../../../../../utils/isValidPhone'
import { DoctorPatientProfile } from '../../types/doctorPatients.types'
import { omit } from 'lodash'

export interface ContactInForm {
  id?: string
  firstName: string
  lastName: string
  phone: string
}

export type ContactFormValues = {
  [contacts in ContactType]: ContactInForm[]
}

export interface ContactModalProps {
  title: string
  contactType: ContactType
  contacts?: contactChore[] | null
}

interface Props {
  props: ContactModalProps
  isOpen: boolean
  onClose: () => void
  patientProfile: DoctorPatientProfile
  refetchPatientProfile: () => void
}

const emptyContact: ContactInForm = {
  firstName: '',
  lastName: '',
  phone: '',
}

export const ContactModal: React.FC<React.PropsWithChildren<Props>> = ({
  props: { title, contactType, contacts },
  isOpen,
  onClose,
  patientProfile,
  refetchPatientProfile,
}) => {
  const { t } = useTranslation()
  const { setSuccessToast } = useNotifications()

  const formMethods = useForm<ContactFormValues>({
    defaultValues: {
      [contactType]:
        (contacts?.length ?? 0) > 0
          ? contacts?.map((contact) => ({
              id: contact.id,
              firstName: contact.firstName,
              lastName: contact.lastName,
              phone: contact.phone,
            }))
          : [emptyContact],
    },
  })

  const { handleSubmit, control, reset } = formMethods

  const {
    fields: contactFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: contactType,
  })

  const handleClose = () => {
    reset()
    onClose()
  }

  const [updatePatientProfileMutation] = useMutation<
    updatePatientProfile,
    updatePatientProfileVariables
  >(UPDATE_PATIENT_PROFILE, {
    onCompleted: () => {
      refetchPatientProfile()
      setSuccessToast('common:saveSuccessful', { useFullKey: true })
      handleClose()
    },
  })

  const saveField = async (values: ContactInForm[]) => {
    const key =
      contactType === 'emergency'
        ? 'emergencyContacts'
        : 'entitleToInfoContacts'
    await updatePatientProfileMutation({
      variables: {
        patientId: patientProfile.id,
        updatePatientInput: {
          emergencyContacts: patientProfile.emergencyContacts?.map((contact) =>
            omit(contact, '__typename')
          ),
          entitleToInfoContacts: patientProfile.entitleToInfoContacts?.map(
            (contact) => omit(contact, '__typename')
          ),
          [key]: values,
        },
      },
    })
  }

  const onSubmit = handleSubmit(async (formValues: ContactFormValues) => {
    saveField(formValues[contactType])
  })

  return (
    <FormProvider {...formMethods}>
      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack
            flexDirection="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            {title}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={() => ({
                color: 'common.black',
              })}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            {contactFields.map((contact, index) => {
              return (
                <Stack key={contact.id ?? index}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" fontWeight="700">
                      {t(`patients:contactType.${contactType}`)}
                      {contactFields.length > 1 && ` (${index + 1})`}
                    </Typography>

                    {contactFields.length > 1 && (
                      <Button
                        variant="text"
                        color="error"
                        startIcon={<DeleteIcon />}
                        sx={{ p: 0.5 }}
                        onClick={() => remove(index)}
                      >
                        {t('common:delete')}
                      </Button>
                    )}
                  </Stack>
                  <Stack direction="row" gap={1.5}>
                    <TextfieldController
                      name={`${contactType}.${index}.lastName`}
                      label={t('patients:contactLastName')}
                      rules={{ required: true }}
                    />
                    <TextfieldController
                      name={`${contactType}.${index}.firstName`}
                      label={t('patients:contactFirstName')}
                      rules={{ required: true }}
                    />
                  </Stack>
                  <TextfieldController
                    name={`${contactType}.${index}.phone`}
                    label={t('patients:contactPhone')}
                    rules={{
                      required: true,
                      pattern: {
                        value: ValidPhonePattern,
                        message: t('messages:warnings.notPhone'),
                      },
                    }}
                  />
                </Stack>
              )
            })}
          </Stack>

          <Button
            size="small"
            color="primary"
            variant="text"
            onClick={() => append(emptyContact)}
            startIcon={<AddIcon />}
            sx={{
              p: 1,
              textAlign: 'left',
            }}
          >
            {t(`patients:addMoreContacts`)}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            {t('common:cancel')}
          </Button>
          <Button color="primary" variant="contained" onClick={onSubmit}>
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  )
}
